import { CollectionTypes } from '../../../../../Generated/CollectionType.js'
import { ExtractionKits } from '../../../../../Generated/ExtractionKit.js'
import { HabitatTypes } from '../../../../../Generated/HabitatType.js'
import { LocalityTypes } from '../../../../../Generated/LocalityType.js'
import { MatrixTypes } from '../../../../../Generated/MatrixType.js'
import { Sequencers } from '../../../../../Generated/Sequencer.js'
import { SequencingChemistrys } from '../../../../../Generated/SequencingChemistry.js'
import { TrapTypes } from '../../../../../Generated/TrapType.js'

export type EnumOption = { key: string; label: string }

/**
 * The metadata CSV columns whose values are a closed set, and the set.
 *
 * Keyed by the column name `buildSamplePoolCsvTable` writes, so the edit grid and the exported file
 * agree by construction. The value sets come from the generated DTOs rather than being restated, so a
 * new enum member reaches the grid the moment `generateTs` runs.
 */
const ENUM_COLUMNS: Record<string, Record<string, { key: string; label: string }>> = {
  matrixType: MatrixTypes,
  extractionKit: ExtractionKits,
  trapType: TrapTypes,
  collectionType: CollectionTypes,
  sequencer: Sequencers,
  sequencingChemistry: SequencingChemistrys,
  localityType: LocalityTypes,
  habitatType: HabitatTypes
}

/** The allowed values for a column, or `undefined` when the column is free text. */
export const enumOptionsFor = (column: string): EnumOption[] | undefined => {
  const dto = ENUM_COLUMNS[column]
  return dto && Object.values(dto).map(({ key, label }) => ({ key, label }))
}

export const isEnumColumn = (column: string): boolean => column in ENUM_COLUMNS

/**
 * Whether a cell holds something the column will accept: a member of the set, empty (say nothing), or
 * the clear token. Anything else is a value the import would reject, and is worth showing as such
 * before the user gets as far as importing.
 */
export const isKnownEnumValue = (column: string, value: string): boolean => {
  const options = enumOptionsFor(column)
  if (!options) return true
  return options.some((o) => o.key === value)
}

/**
 * Which data column an overlay editor was opened on.
 *
 * The coordinate the overlay carries counts glide's *mangled* columns — the row-marker column is one
 * of them — while `getCellContent` is handed unmangled ones. Taking the overlay's index as a data
 * index therefore lands one column to the right, which showed `trapType` the values for
 * `collectionType`.
 *
 * Rather than trust an offset, this checks the candidate against the value the overlay is actually
 * editing and returns nothing when they disagree: a wrong dropdown is worse than the plain text
 * editor, because it invites a choice the column will reject.
 */
export const dataColumnOfOverlay = (opts: {
  overlayColumn: number
  markerColumns: number
  columnsOrder: string[]
  row: Record<string, string> | undefined
  cellText: string
}): string | undefined => {
  const { overlayColumn, markerColumns, columnsOrder, row, cellText } = opts
  const column = columnsOrder[overlayColumn - markerColumns]
  if (column === undefined) return undefined
  if (row && (row[column] ?? '') !== cellText) return undefined
  return column
}
